/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Container, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { StyledSlider } from "./CustomSlider";
import { useEffect, useState } from "react";
import { amountToText, calculateAmountMonthly, formatCurrency } from "@helpers/funciones";
import { ApiError, LoanCondiciones } from "@interfaces/response";
import { TaxSystem } from "@interfaces/catalogs";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { usePostDocumentQuotationTableMutation } from "@api/documents";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useAppDispatch } from "@store/store";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";

// se le suma 0.01 para que el slider no se quede en el minimo, ya que el slider no permite valores iguales
const MAGIC_QUANTITY = 0.01;

interface QuotationProps {
  condition: LoanCondiciones | null;
  taxSystems: TaxSystem | null;
  onUpdateValues: (amount: number, term: number, monthlyRate: number) => void;
}

interface SliderParams {
  value: number;
  min: number;
  max: number;
  step: number;
  marks: { value: number; label: string }[];
}

export const QuotationOferta = (props: QuotationProps) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  const { condition, taxSystems } = props;
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [stepTerm, setStepTerm] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const interfaceConfig = useInterfaceConfig();
  const [postGenerateQuotationTable] = usePostDocumentQuotationTableMutation();

  const [paramsTerm, setParamsTerm] = useState<SliderParams>({
    value: 0,
    min: 0,
    max: 0,
    step: 0,
    marks: [],
  });
  const [paramsAmount, setParamsAmount] = useState<SliderParams>({
    value: 0,
    min: 0,
    max: 0,
    step: 0,
    marks: [],
  });

  const handleAmountChange = (_: any, newValue: number | number[]) => {
    setParamsAmount((prev) => ({ ...prev, value: newValue as number }));
  };

  const handleTermChange = (_: any, newValue: number | number[]) => {
    setParamsTerm((prev) => ({ ...prev, value: newValue as number }));
  };

  useEffect(() => {
    const calculatePayment = () => {
      if (!condition || !taxSystems) {
        return;
      }

      const monthly_payment = calculateAmountMonthly(
        paramsAmount.value,
        paramsTerm.value,
        condition.interest_rate || interfaceConfig.interest_rate,
      );
      props.onUpdateValues(paramsAmount.value, paramsTerm.value, monthly_payment);
      setMonthlyRate(monthly_payment);
    };

    calculatePayment();
  }, [paramsTerm.value, paramsAmount.value]);

  useEffect(() => {
    const initialValues = () => {
      if (!condition || !taxSystems) {
        return;
      }

      const { min_amount, max_amount, jump_amount, jump_term } = taxSystems;
      const {approved_amount, selected_amount, selected_term, approved_min_term, approved_max_term } = condition;
      const result_amount: SliderParams = {
        value: 0,
        min: 0,
        max: 0,
        step: jump_amount,
        marks: [],
      };
      const result_term: SliderParams = {
        value: 0,
        min: 0,
        max: 0,
        step: jump_term,
        marks: [],
      };

      if (approved_amount < min_amount) {
        // monto aprobado menor al minimo, se queda en 0
        result_amount.value = selected_amount;
        result_amount.min = selected_amount - MAGIC_QUANTITY;
        result_amount.max = selected_amount;
        result_amount.marks = [
          { value: selected_amount - MAGIC_QUANTITY, label: amountToText(selected_amount) },
          { value: selected_amount, label: amountToText(selected_amount) },
        ];
      } else if (approved_amount == min_amount) {
        // monto aprobado igual al minimo
        result_amount.value = approved_amount;
        result_amount.min = min_amount - MAGIC_QUANTITY;
        result_amount.max = min_amount;
        result_amount.marks = [
          { value: min_amount - MAGIC_QUANTITY, label: amountToText(min_amount) },
          { value: min_amount, label: amountToText(min_amount) },
        ];
      } else {
        // monto aprobado mayor al minimo
        const max_value = approved_amount <= max_amount ? approved_amount : max_amount;
        result_amount.value = selected_amount;
        result_amount.min = min_amount;
        result_amount.max = max_value;
        result_amount.marks = [];

        for (let i = min_amount; i <= max_value; i += jump_amount) {
          result_amount.marks.push({ value: i, label: "" });
        }

        const first_item = result_amount.marks[0];
        const last_item = result_amount.marks[result_amount.marks.length - 1];

        result_amount.marks[0] = {
          value: first_item.value,
          label: `${amountToText(first_item.value)} ${interfaceConfig.cotizador?.current_currency || ""}`,
        };

        result_amount.marks[result_amount.marks.length - 1] = {
          value: last_item.value,
          label: `${amountToText(last_item.value)} ${interfaceConfig.cotizador?.current_currency || ""}`,
        };
      }

      const max_value = approved_max_term;
      result_term.value = selected_term;
      result_term.min = approved_min_term;
      result_term.max = max_value;
      result_term.marks = [];

      for (let i = approved_min_term; i <= max_value; i += jump_term) {
        result_term.marks.push({ value: i, label: i?.toString() });
      }

      if (result_term.step >= 20) {
        setStepTerm(result_term.step);
      } else {
        setStepTerm(1);
      }

      setParamsTerm(result_term);
      setParamsAmount(result_amount);
    };

    initialValues();
  }, [condition, taxSystems]);

  const genarateQuotationTable = async () => {
    try {
      setIsLoading(true);
      const responseBase64 = await postGenerateQuotationTable({
        amount: paramsAmount.value,
        term: paramsTerm.value,
        company: interfaceConfig?.name || "",
      }).unwrap();

      const byteCharacters = atob(String(responseBase64));
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const file = new Blob([byteArray], { type: "application/pdf" });

      const fileURL = URL.createObjectURL(file);

      const link = document.createElement("a");
      link.href = fileURL;
      link.download = "tabla_amortizacion.pdf";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    } catch (error) {
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box textAlign={"center"} width={"100%"}>
      <Container sx={{ mt: 2, mb: 2 }}>
        <Typography
          fontWeight={theme?.palette?.fontWeights?.medium}
          variant={isMobileOrTablet ? "body2" : "body1"}
          mt={2}
          color={theme?.palette?.primary?.dark}
        >
          Selecciona el monto
        </Typography>
        <Typography
          textAlign={"center"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          fontWeight={theme?.palette?.fontWeights?.semibold}
          color={theme?.palette?.primary?.dark}
        >
          {amountToText(paramsAmount.value)} {interfaceConfig.cotizador?.current_currency || "MXN"}
        </Typography>
        {condition && taxSystems ? (
          <>
            <StyledSlider
              aria-label="amount"
              value={paramsAmount.value}
              min={paramsAmount.min}
              max={paramsAmount.max}
              step={paramsAmount.step}
              marks={paramsAmount.marks}
              onChange={handleAmountChange}
            />
          </>
        ) : (
          <Skeleton variant="rectangular" animation="wave" />
        )}

        <Typography
          fontWeight={theme?.palette?.fontWeights?.medium}
          variant={isMobileOrTablet ? "body2" : "body1"}
          mt={4}
          mb={2}
          color={theme?.palette?.primary?.dark}
        >
          Selecciona el plazo en {taxSystems?.unit_time_term}
        </Typography>
        {condition && taxSystems ? (
          <StyledSlider
            aria-label="term"
            value={paramsTerm.value}
            min={paramsTerm.min}
            max={paramsTerm.max}
            step={stepTerm}
            marks={paramsTerm.marks}
            onChange={handleTermChange}
            valueLabelDisplay="on"
          />
        ) : (
          <Skeleton variant="rectangular" animation="wave" />
        )}
      </Container>

      <Typography
        textAlign={"center"}
        color={theme?.palette?.primary?.main}
        fontWeight={theme?.palette?.fontWeights?.bold}
        variant={isMobileOrTablet ? "body2" : "body1"}
      >
        Pago mensual estimado
      </Typography>
      <Typography
        mb={3}
        textAlign={"center"}
        variant="h4"
        fontWeight={theme?.palette?.fontWeights?.semibold}
        color={theme?.palette?.primary?.dark}
      >
        {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
        <Typography
          variant="h5"
          component={"span"}
          fontWeight={theme?.palette?.fontWeights?.semibold}
          color={theme?.palette?.primary?.dark}
        >
          {interfaceConfig.cotizador?.current_currency || "MXN"}
        </Typography>
      </Typography>

      {interfaceConfig.cotizador?.has_quotation_table && (
        <>
          <Box
            display="flex"
            justifyContent="center"
            gap={1}
            sx={{
              cursor: isLoading ? "not-allowed" : "pointer",
              textDecoration: isLoading ? "none" : "underline",
              opacity: isLoading ? 0.5 : 1,
              pointerEvents: isLoading ? "none" : "auto",
              mb: 2,
            }}
            onClick={() => {
              if (!isLoading) {
                genarateQuotationTable();
              }
            }}
          >
            <SystemUpdateAltIcon sx={{ color: theme?.palette?.primary?.dark }} />
            <Typography fontSize={"14px"} textAlign={"center"} color={theme?.palette?.primary?.dark}>
              Descarga aquí la tabla de amortización informativa
            </Typography>
          </Box>
        </>
      )}

      <Typography
        textAlign={"center"}
        color={theme?.palette?.primary?.dark}
        variant={isMobileOrTablet ? "body3" : "body2"}
        mb={2}
      >
        {interfaceConfig.cotizador?.estimated_amount_text || "*Mensualidad estimada"}
      </Typography>
    </Box>
  );
};
